//Built-in libraries

//Third party libraries
import { PlusIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useEffect, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import InputError from '@/components/UI/Form/Error';
import Input from '@/components/UI/Form/Input';
import RadioGroupInput from '@/components/UI/Form/RadioGroup';
import Confirm from '@/components/UI/Modal/Confirm';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';

//STP Libraries

import ReactSelect from '@/components/UI/Form/ReactSelect';
import type { ReactSelectOption } from '@/types/Constants';
//Assets and Miscellaneous
import { initialValues, tenantTypes } from './constants';
import type { ITenancy } from './types';

//Component Begin

const Tenancy = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editTenantId, setEditTenantId] = useState<string>('');
  const [selectedTenant, setSelectedTenant] = useState<ITenancy>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [tenant, setTenant] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  useEffect(() => {
    //clearing error message
    if (tenant.willIncreaseRent.value === 'no') {
      setTenant((prevState) => {
        return {
          ...prevState,
          newRent: {
            ...prevState.newRent,
            value: undefined,
            error: '',
          },
        };
      });
    }
  }, [tenant.willIncreaseRent]);

  const resetForm = () => {
    setTenant(initialValues);
  };

  const editTenantHandler = (t: any) => {
    setIsEditing(true);
    const formData = Object.keys(tenant).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...tenant[next],
          value: t[next],
          isTouched: !!t[next],
        },
      };
    }, {});
    setTenant(formData);
    setEditTenantId(t.id);
  };

  const updateTenantHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(tenant).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: tenant[next].value,
            id: editTenantId,
          };
        },
        {}
      );
      updateData({
        step2: {
          ...data.step2,
          section2: data.step2.section2.map((p: any) => {
            if (p.id === editTenantId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeTenantHandler = (tenant: ITenancy) => {
    setShowRemoveConfirm(true);
    setSelectedTenant(tenant);
  };
  const confirmRemoveTenant = async () => {
    setShowRemoveConfirm(false);
    const filteredTenant = data.step2.section2.filter((tenant: any) => tenant.id !== selectedTenant?.id);
    updateData({
      step2: {
        ...data.step2,
        section2: filteredTenant,
      },
    });

    if (selectedTenant?.id === editTenantId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveTenant = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...tenant };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      let rules = { ...formData[next].rules };

      //update rules for dependent field
      if (
        formData[next]?.isDependent &&
        formData[next]?.isDependent.dependentValue === formData[formData[next]?.isDependent.fieldName].value
      ) {
        rules = { ...formData[next].isDependent.rules };
      }

      if (!formData[next].isTouched && rules.required) {
        setTenant((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }

      return { ...prev };
    }, {});
    return isObjectEmpty(formDataErrors);
  };

  const onAddTenantHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(tenant).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: tenant[next].value };
        },
        {}
      );
      updateData({
        step2: {
          ...data.step2,
          section2: [...data.step2.section2, { ...formattedData, id: uuidv4() }],
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = createInputChangedHandler(setTenant, tenant);

  const [hasTenancy, setHasTenancy] = useState('no');

  const onTenancyChange = (value: string) => {
    if (value === 'no') {
      updateData({
        step2: {
          ...data.step2,
          section2: [],
        },
      });
      resetForm();
    }
    setHasTenancy(value);
  };

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove tenant'
          message={`You are about to remove ${selectedTenant?.tenantName} tenant. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveTenant}
          decline={cancelRemoveTenant}
        />
      )}

      <section className='space-y-4'>
        <RadioGroupInput
          label='Are there currently tenants in the building?'
          value={hasTenancy}
          onChange={onTenancyChange}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
        {hasTenancy === 'yes' && (
          <>
            <h3>Current Tenancy</h3>
            {data.step2?.section2.length > 0 && (
              <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
                <table className='divide-y divide-gray-200 w-full '>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Tenant Name</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Unit Number</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Type</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Current Monthly Rent</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider w-1/5'>
                        Will building renovations result in increased rent?
                      </th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>New Monthly Rent</th>
                      <th className=''></th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {data.step2.section2.map((tenant: ITenancy) => (
                      <tr key={tenant.id} className={`${tenant.id === editTenantId ? 'bg-neutralColor-lighter' : ''}`}>
                        <td className='p-4'>{tenant.tenantName}</td>
                        <td className='p-4'>{tenant.unitNumber}</td>
                        <td className='p-4'>{tenant.tenantType}</td>
                        <td className='p-4'>
                          <CurrencyFormatDisplay value={tenant.currentRent ?? 0} />
                        </td>
                        <td className='p-4 capitalize'>{tenant.willIncreaseRent}</td>
                        <td className='p-4'>
                          {tenant.willIncreaseRent === 'yes' ? <CurrencyFormatDisplay value={tenant.newRent ?? 0} /> : 'N/A'}
                        </td>
                        <td className='flex justify-end gap-4 px-6 py-4'>
                          <div className='cursor-pointer hover:text-blue-500' onClick={() => editTenantHandler(tenant)}>
                            Edit
                          </div>
                          <div className='cursor-pointer hover:text-tertiaryColor-normal' onClick={() => removeTenantHandler(tenant)}>
                            Delete
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className='w-full grid grid-cols-6 gap-6'>
              <Input
                customClassName='col-span-3'
                label='Tenant Name'
                id='tenant-name'
                name='tenantName'
                type='text'
                value={tenant.tenantName.value}
                error={tenant?.tenantName?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
              <Input
                customClassName='col-span-3'
                label='Unit Number'
                id='unit-number'
                name='unitNumber'
                type='text'
                value={tenant.unitNumber.value}
                error={tenant?.unitNumber?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
              <div className='col-span-3'>
                <ReactSelect
                  label='Type'
                  id='tenant-type'
                  name='tenantType'
                  placeholder='Select'
                  options={tenantTypes.map((t) => ({
                    label: t,
                    value: t,
                  }))}
                  value={{
                    label: tenant.tenantType.value,
                    value: tenant.tenantType.value,
                  }}
                  error={tenant?.tenantType?.error}
                  onChange={(option) => {
                    inputChangedHandler((option as ReactSelectOption).value, 'tenantType');
                  }}
                  onBlur={(_e) => {
                    inputChangedHandler(tenant.tenantType.value, 'tenantType');
                  }}
                />
              </div>
              <div className='col-span-3'>
                <label className='leading-7 block text-sm font-medium text-neutralColor-medium' htmlFor='current-rent'>
                  Current Monthly Rent ($)
                </label>
                <NumberFormat
                  name='currentRent'
                  id='current-rent'
                  value={tenant.currentRent.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  thousandSeparator={true}
                  decimalScale={2}
                  valueIsNumericString
                  prefix={'$ '}
                  maxLength={21}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'currentRent');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(tenant.currentRent.value, e.target.name);
                  }}
                />
                {tenant?.currentRent?.error && <InputError>{tenant?.currentRent?.error}</InputError>}
              </div>
              <RadioGroupInput
                className='col-span-6'
                label='Will building renovations result in increased rent?'
                id='will-increase-rent'
                name='willIncreaseRent'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
                value={tenant.willIncreaseRent.value}
                error={tenant?.willIncreaseRent?.error}
                onChange={(value) => {
                  inputChangedHandler(value, 'willIncreaseRent');
                }}
                onBlur={(_e) => {
                  inputChangedHandler(tenant.willIncreaseRent.value, 'willIncreaseRent');
                }}
              />

              {tenant.willIncreaseRent.value === 'yes' && (
                <div className='col-span-2'>
                  <label className='leading-7 block text-sm font-medium text-neutralColor-medium' htmlFor='current-rent'>
                    New Monthly Rent ($)
                  </label>
                  <NumberFormat
                    name='newRent'
                    id='new-rent'
                    value={tenant.newRent.value ?? ''}
                    className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                    thousandSeparator={true}
                    decimalScale={2}
                    valueIsNumericString
                    prefix={'$ '}
                    maxLength={21}
                    onValueChange={(target, s) => {
                      if (s.source !== 'prop') {
                        inputChangedHandler(target.floatValue, 'newRent');
                      }
                    }}
                    onBlur={(e: any) => {
                      inputChangedHandler(tenant.newRent.value, e.target.name);
                    }}
                  />
                  {tenant?.newRent?.error && <InputError>{tenant?.newRent?.error}</InputError>}
                </div>
              )}
            </div>
            {!isEditing && <Button icon={<PlusIcon />} label='Add' type='button' btnStyle='primary' onClick={onAddTenantHandler} />}
            {isEditing && (
              <div className='flex gap-4'>
                <Button label='Save' type='button' btnStyle='primary' onClick={updateTenantHandler} />
                <Button
                  label='Cancel'
                  type='button'
                  btnStyle='neutral'
                  onClick={() => {
                    resetForm();
                    setIsEditing(false);
                    setEditTenantId('');
                  }}
                />
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default Tenancy;
